import { getUrlSync, ICoreContext } from '@msdyn365-commerce/core';
import { getCartStateData, GetCartStateDataInput, ICartState } from '@msdyn365-commerce/global-state';
import { format } from '@msdyn365-commerce-modules/utilities';
import { IAppSettingsInContext } from '../settings/app.settings.autogenerated';
import { ProductSearchResultExt, SimpleProductExt } from './types';
import { toast, toasts } from '../components/toast/toast';
import { validateAlcoholLicenseAsync } from '../actions/validate-alcohol-license.action';
import { getDefaultBuyQuantity } from './common-utils';
import { BuyValidationResult } from '../components/product-list-component/product-list-component';
import { getLocale } from '../components/product-list-component/locale';
import * as GTM from './google-tag-manager/gtm';
import { safeAdd } from './math';

// Static class with helper functions for cart operations
export namespace CartHelper {
    let locale: ReturnType<typeof getLocale> | undefined;
    let cartUrl: string | undefined;

    export async function getCartWaitForUpdate(context: ICoreContext<IAppSettingsInContext>): Promise<ICartState | undefined> {
        const input = new GetCartStateDataInput(context.request.apiSettings, context.request);

        let cart = await getCartStateData(input, context.actionContext);

        if (!cart) {
            return;
        }

        while (cart.status === 'UPDATING') {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            cart = await getCartStateData(input, context.actionContext);

            // Shouldn't happen, but just in case to avoid infinite loop
            if (!cart) {
                return;
            }
        }

        return cart;
    }

    function showGetCartError(
        text: string = 'Failed to get cart. Please refresh and try again. Contact support if the issue persists.',
        header: string = 'Error'
    ): void {
        toast({
            header,
            text,
            autoClose: 30000,
            canClose: true,
            position: 'bottom-right',
            showProgress: true
        });
    }

    type AddToCartArgs = [
        context: ICoreContext<IAppSettingsInContext>,
        product: ProductSearchResultExt,
        simpleProduct: SimpleProductExt,
        userHasAccess?: boolean,
        addQuantity?: number,
        breakoff?: boolean,
        onBuyValidate?: () => Promise<BuyValidationResult>
    ];
    export async function addToCartAsync(...args: AddToCartArgs): Promise<boolean> {
        const [context, product, simpleProduct, userHasAccessOrg, addQuantity, breakoff, onBuyValidate] = args;
        const cart = await getCartWaitForUpdate(context);

        if (!cart) {
            showGetCartError();
            return false;
        }

        locale = locale || getLocale(context);
        cartUrl = cartUrl || getUrlSync('cart', context.actionContext);

        let userHasAccess: boolean | undefined = userHasAccessOrg || true; // TODO: Remove 'true' default here to re-enable alcohol license check
        let validationError: string | undefined;

        if (userHasAccess === undefined) {
            const result = await validateAlcoholLicenseAsync(context.actionContext, {
                CatalogId: context.request.apiSettings.catalogId,
                ProductIds: [simpleProduct.RecordId]
            });
            userHasAccess = result.ValidationResult;
            validationError = locale.toastStrings.noAccessToProduct;
        }

        const quantity = addQuantity || getDefaultBuyQuantity(simpleProduct, !!breakoff);

        if (userHasAccess && onBuyValidate) {
            const result = await onBuyValidate();
            userHasAccess = result.isValid;
            validationError = result.message;
        }

        if (userHasAccess !== true) {
            toasts.error(locale.toastStrings.errorHeader, validationError || 'Unknown error');
            return false;
        }

        const cartLine = cart.cart.CartLines?.find(x => x.ProductId === product.RecordId);

        try {
            const result = !cartLine?.LineId
                ? await cart.addProductToCart({ product: simpleProduct, count: quantity, customPrice: product.Price })
                : await cart.updateCartLineQuantity({ cartLineId: cartLine.LineId, newQuantity: safeAdd(cartLine?.Quantity || 0, quantity) });

            if (result.status === 'SUCCESS') {
                void GTM.addToCartAsync(product, quantity);

                toasts.success(
                    undefined,
                    format(locale.toastStrings.productsAddedToCart, quantity, product.Name ?? ''),
                    {
                        link: cartUrl,
                        linkLabel: locale.toastStrings.goToCart
                    }
                );
            } else if (result.status === 'FAILED') {
                const text = result.validationResults?.[0]?.LocalizedMessage || result.errorDetails?.LocalizedMessage || 'Unknown error adding items to cart.';
                toasts.error(locale.toastStrings.errorHeader, text);
                return false;
            }
        } catch (err) {
            const error = err as unknown as any;
            const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
            toasts.error(locale.toastStrings.errorHeader, error, { footer });
            return false;
        }

        return true;
    }
}
